import { modifier } from 'ember-modifier';
import type { ChartDataPoint, ChartType, ChartOptions } from '../types/chart.ts';
import { ChartRenderer } from './chart-renderer.ts';

export { ChartSpecBuilder } from './chart-specs.ts';
export { ChartRenderer } from './chart-renderer.ts';
export { ChartDataTransformer } from './chart-data-transformer.ts';
export { OpenSearchFieldMapper } from './opensearch-field-mapper.ts';
export { DateGroupingUtility } from './date-grouping.ts';

// Chart types
export type {
  ChartDataPoint,
  VegaLiteSpec,
  ChartType,
  ChartOptions,
  ChartRenderResult,
} from '../types/chart.ts';

// Field mapping types
export type { FieldOption, OpenSearchMapping } from './opensearch-field-mapper.ts';

// Date grouping types
export type { DateGroupingInterval } from './date-grouping.ts';

/**
 * Reusable chart modifier with ResizeObserver for responsive charts
 *
 * Usage:
 *   import { setupChart } from 'tio-common/utils/chart-utils';
 *
 *   // Basic usage
 *   <div {{setupChart this.chartData "donut"}}>
 *
 *   // With options
 *   <div {{setupChart this.chartData "donut" width=300 height=300}}>
 *
 *   // With click handler
 *   <div {{setupChart this.chartData "donut" onClick=this.handleClick}}>
 *
 * @param element - DOM element to attach the chart to
 * @param positional - [data, chartType]
 * @param named - Chart options
 */
export const setupChart = modifier(
  (
    element: HTMLElement,
    [data, chartType = 'donut' as ChartType]: [ChartDataPoint[], ChartType?],
    options: ChartOptions = {}
  ) => {
    let resizeTimeout: ReturnType<typeof setTimeout> | undefined;

    // Function to render chart with current data
    const render = async () => {
      if (data && data.length > 0) {
        await ChartRenderer.render(element, data, chartType, options);
      }
    };

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        render();
      }, 150);
    });

    resizeObserver.observe(element);

    // Trigger initial rendering
    render();

    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimeout);
    };
  }
);
